// A window that floats over the page WITHOUT a backdrop.
//
// The modal (`openModal`) dims and blocks the whole page, which is right for a form that
// must be finished or cancelled. It is wrong for a tool you keep open WHILE using the page
// behind it — the flow editor's device screen, where the author taps an element on the
// phone and then edits the step it landed in, or picks a phone while a run is going. This
// is that other thing: a card pinned over the page, dragged around by its header, closed by
// its own ✕, and never in the way of the page underneath.
//
// It lives in <body>, outside the SPA's `content` element, so a re-render of the page does
// not take it down — which also means nothing would take it down when the user navigates
// away. `anchor` is the answer: the element the window belongs to; the window closes itself
// once that element leaves the document.
import { esc } from './modal';
/** The window on top gets the highest of these; each open/click bumps it. */
let zTop = 300;
export function createFloatWindow(opts) {
    const el = document.createElement('div');
    el.className = `fw${opts.className ? ` ${opts.className}` : ''}`;
    el.hidden = true;
    el.innerHTML = `
        <div class="fw__head">
            <span class="fw__title">${esc(opts.title)}</span>
            <span class="fw__actions"></span>
            <button type="button" class="fw__close" title="${esc('×')}" aria-label="close">×</button>
        </div>
        <div class="fw__body"></div>
        ${opts.resizable === false ? '' : '<div class="fw__grip" aria-hidden="true"></div>'}
    `;
    const head = el.querySelector('.fw__head');
    const body = el.querySelector('.fw__body');
    const actions = el.querySelector('.fw__actions');
    const grip = el.querySelector('.fw__grip');
    document.body.appendChild(el);
    // Capped under the modal backdrop (400): a real modal opened from the page must still
    // cover every floating window, however many times they were clicked.
    const raise = () => { zTop = Math.min(399, zTop + 1); el.style.zIndex = String(zTop); };
    el.addEventListener('pointerdown', raise);
    // Drag by the header. Pointer capture keeps the move events coming when the cursor
    // outruns the header mid-drag; the card is kept on screen so it cannot be dragged
    // somewhere it can no longer be grabbed from.
    let drag = null;
    head.addEventListener('pointerdown', (e) => {
        if (e.target.closest('.fw__close, .fw__actions'))
            return;
        const r = el.getBoundingClientRect();
        drag = { dx: e.clientX - r.left, dy: e.clientY - r.top };
        head.setPointerCapture(e.pointerId);
        e.preventDefault();
    });
    head.addEventListener('pointermove', (e) => {
        if (!drag)
            return;
        const w = el.offsetWidth, h = el.offsetHeight;
        const x = Math.min(Math.max(0, e.clientX - drag.dx), Math.max(0, window.innerWidth - w));
        const y = Math.min(Math.max(0, e.clientY - drag.dy), Math.max(0, window.innerHeight - Math.min(h, 80)));
        el.style.left = `${x}px`;
        el.style.top = `${y}px`;
        el.style.right = 'auto';
    });
    const endDrag = () => { drag = null; };
    head.addEventListener('pointerup', endDrag);
    head.addEventListener('pointercancel', endDrag);
    // Resize by the bottom-right grip. The card opens anchored to `right`, so the grip
    // pins it to its current top-left first — otherwise widening it would push the far
    // edge away instead of growing towards the cursor.
    const MIN_W = 280, MIN_H = 200;
    const applySize = (w, h) => {
        const r = el.getBoundingClientRect();
        el.style.width = `${Math.min(Math.max(MIN_W, w), Math.max(MIN_W, window.innerWidth - r.left - 8))}px`;
        el.style.height = `${Math.min(Math.max(MIN_H, h), Math.max(MIN_H, window.innerHeight - r.top - 8))}px`;
        // An explicit height and the stylesheet's viewport cap would fight each other; the
        // clamps above are what keeps the card on screen now.
        el.style.maxHeight = 'none';
    };
    const sizeStore = opts.sizeKey ? `fw-size:${opts.sizeKey}` : null;
    const rememberSize = () => {
        if (!sizeStore)
            return;
        // Storage can be blocked (private window, site data off); the size then simply
        // does not survive a reload, which is not worth failing the resize over.
        try {
            localStorage.setItem(sizeStore, JSON.stringify({ w: el.offsetWidth, h: el.offsetHeight }));
        }
        catch { /* ignore */ }
    };
    const restoreSize = () => {
        if (!sizeStore)
            return;
        try {
            const saved = JSON.parse(localStorage.getItem(sizeStore) ?? 'null');
            // A size saved on a wider screen is clamped by applySize, not carried over.
            if (saved?.w && saved.h)
                applySize(saved.w, saved.h);
        }
        catch { /* corrupt or blocked — open at the CSS size */ }
    };
    let resize = null;
    grip?.addEventListener('pointerdown', (e) => {
        const r = el.getBoundingClientRect();
        el.style.left = `${r.left}px`;
        el.style.top = `${r.top}px`;
        el.style.right = 'auto';
        resize = { x: e.clientX, y: e.clientY, w: r.width, h: r.height };
        grip.setPointerCapture(e.pointerId);
        e.preventDefault();
    });
    grip?.addEventListener('pointermove', (e) => {
        if (!resize)
            return;
        applySize(resize.w + (e.clientX - resize.x), resize.h + (e.clientY - resize.y));
    });
    const endResize = () => {
        if (!resize)
            return;
        resize = null;
        rememberSize();
    };
    grip?.addEventListener('pointerup', endResize);
    grip?.addEventListener('pointercancel', endResize);
    // Watches the anchor while the window is open — a page that is re-rendered in place
    // keeps its anchor, a page that is left loses it.
    let watcher = null;
    const watch = () => {
        if (!opts.anchor || watcher)
            return;
        watcher = new MutationObserver(() => { if (!opts.anchor.isConnected)
            close(); });
        watcher.observe(document.body, { childList: true, subtree: true });
    };
    const unwatch = () => { watcher?.disconnect(); watcher = null; };
    // Restored on the first open, not at create: the card is `hidden` until then and has
    // no rect for applySize to clamp against.
    let restored = false;
    function open() {
        el.hidden = false;
        if (!restored) {
            restored = true;
            restoreSize();
        }
        raise();
        watch();
    }
    function close() {
        if (el.hidden)
            return;
        el.hidden = true;
        unwatch();
        opts.onClose?.();
    }
    el.querySelector('.fw__close').addEventListener('click', close);
    return {
        body,
        actions,
        open,
        close,
        toggle() { if (el.hidden)
            open();
        else
            close(); },
        isOpen() { return !el.hidden; },
        destroy() { close(); el.remove(); },
    };
}
